import { useRef, useState } from "react";
import dynamic from "next/dynamic";
import { MilkdownProvider } from "@milkdown/react";
import { ComplexMarkdownEditor } from "@/components/Form/ComplexMarkdownField/ComplexMarkdownField";
import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import { Textarea } from "@/components/shadcn/textarea";
import { Switch } from "@/components/shadcn/switch";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { Calendar as CalendarComponent } from "@/components/shadcn/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import { getSpotFileUrl } from "@/utils/generic";
import { StorageService } from "@/services/Storage/storage";
import { SpotToast } from "@/components/SpotToast";
import { useSpotUser } from "@/utils/spotUser";

// Lazy-loaded with SSR disabled because the dialog transitively imports
// pdf.js, which references DOMMatrix at module-evaluation time and crashes
// during server rendering.
const DocumentPreviewDialog = dynamic(
  () =>
    import("@/components/CaseManagement/DocumentPreviewDialog").then(
      (m) => m.DocumentPreviewDialog
    ),
  { ssr: false }
);
import { Calendar, FileText, Download, Eye, X, Upload } from "lucide-react";
import type { TableSelectSource } from "@/generated";
import type { FieldDef, TableColumn } from "./types";
import { LookupCellCombobox } from "./LookupCellCombobox";
import { formatDateOnly, parseDateOnly } from "./dateOnly";
import { normalizeTableRows } from "./tableRows";
import { TableFieldGrid } from "./TableFieldGrid";

export interface FieldWidgetProps {
  field: FieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
  readOnly?: boolean;
  /**
   * When true, text-like inputs fill their parent width. Case fields use
   * this in grouped cards; extraction viewer also wants full width.
   */
  compact?: boolean;
  /**
   * Fired per keystroke for draft-buffered inputs (which only call
   * `onChange` on blur). Lets callers run live validation without
   * changing the one-save-per-editing-session commit behavior.
   */
  onDraftChange?: (value: unknown) => void;
  /** Paint a validation ring: red for strict violations, amber for lenient. */
  invalid?: "error" | "warning" | null;
  /**
   * Table fields only: localStorage key for persisting user-resized column
   * widths. Omit to keep resizing session-local.
   */
  tableWidthStorageKey?: string;
}

export const invalidRingClass = (
  invalid?: "error" | "warning" | null
): string =>
  invalid === "error"
    ? " ring-1 ring-destructive/70 focus:ring-destructive/70 focus-visible:ring-destructive/70 focus-within:ring-destructive/70"
    : invalid === "warning"
      ? " ring-1 ring-warning-500/70 focus:ring-warning-500/70 focus-visible:ring-warning-500/70 focus-within:ring-warning-500/70"
      : "";

function isFileValue(value: unknown): value is {
  uri: string;
  name: string;
  content_type?: string;
  size?: number;
} {
  return (
    typeof value === "object" &&
    value !== null &&
    "uri" in (value as Record<string, unknown>)
  );
}

function getTableColumns(field: FieldDef): TableColumn[] {
  const options = field.options;
  if (!Array.isArray(options)) return [];
  return options.filter(
    (o): o is TableColumn => typeof o === "object" && o !== null
  );
}

function getSelectOptions(field: FieldDef): string[] {
  const options = field.options;
  if (!Array.isArray(options)) return [];
  return options.filter((o): o is string => typeof o === "string");
}

function getFieldLookupSource(field: FieldDef): TableSelectSource | null {
  const options = field.options;
  return typeof options === "object" &&
    options !== null &&
    !Array.isArray(options) &&
    options.kind === "table"
    ? options
    : null;
}

const ICON_CLASS = "h-3 w-3 text-muted-foreground/60 shrink-0";

const INPUT_TYPE_BY_FIELD: Record<string, string> = {
  number: "number",
  email: "email",
  url: "url",
  phone: "tel",
};

const SELECT_NONE = "__none__";

const MarkdownFieldCell = ({
  initialValue,
  onCommit,
  readOnly,
}: {
  initialValue: string;
  onCommit: (v: string) => void;
  readOnly?: boolean;
}) => {
  const [draft, setDraft] = useState(initialValue);
  // The editor's blur handler serializes the final markdown and fires
  // onChange + onBlur in the same tick, so the onBlur closure would still
  // see the previous draft — read the committed value from a ref instead.
  const draftRef = useRef(initialValue);
  return (
    <MilkdownProvider>
      <ComplexMarkdownEditor
        value={draft}
        onChange={(v) => {
          draftRef.current = v;
          setDraft(v);
        }}
        isDisabled={readOnly}
        inputs={[]}
        minRows={3}
        maxRows={10}
        onBlur={() => {
          if (draftRef.current !== initialValue) onCommit(draftRef.current);
        }}
      />
    </MilkdownProvider>
  );
};

const FileFieldCell = ({
  value,
  onChange,
  readOnly,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  readOnly?: boolean;
}) => {
  const { spotGroup } = useSpotUser();
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileValue = isFileValue(value) ? value : null;

  const handleFileSelect = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const data = new FormData();
        data.append("file", file);
        if (file.lastModified) {
          data.append(
            "client_last_modified",
            new Date(file.lastModified).toISOString()
          );
        }
        const result = await StorageService.uploadFile(
          spotGroup.id,
          data,
          false
        );
        onChange({
          uri: `spot://${result.id}`,
          name: result.filename,
          content_type: result.content_type,
        });
      } catch {
        SpotToast.error("Failed to upload file");
      }
    };
    input.click();
  };

  return (
    <div className="flex items-center gap-2 h-8">
      {fileValue ? (
        <>
          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-sm text-muted-foreground truncate max-w-[200px]">
            {fileValue.name}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={() => setPreviewOpen(true)}
          >
            <Eye className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            asChild
          >
            <a
              href={getSpotFileUrl(fileValue.uri, false)}
              download={fileValue.name}
            >
              <Download className="h-3 w-3" />
            </a>
          </Button>
          {!readOnly && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={() => onChange(null)}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          {previewOpen && (
            <DocumentPreviewDialog
              attachment={{
                ...fileValue,
                size:
                  fileValue.size != null ? String(fileValue.size) : undefined,
              }}
              isOpen={previewOpen}
              onClose={() => setPreviewOpen(false)}
            />
          )}
        </>
      ) : readOnly ? (
        <span className="text-sm text-muted-foreground">No file</span>
      ) : (
        <button
          type="button"
          onClick={handleFileSelect}
          className="flex items-center gap-2 h-8 w-full px-2 text-sm text-muted-foreground rounded-md border-0 shadow-[0_0_0_1px_var(--input),0_1px_2px_0_rgba(0,0,0,0.05)] bg-transparent hover:shadow-[0_0_0_1px_var(--border),0_1px_2px_0_rgba(0,0,0,0.05)] hover:bg-background transition-all cursor-pointer"
        >
          <Upload className="h-3.5 w-3.5" />
          Choose file
        </button>
      )}
    </div>
  );
};

/**
 * Single renderer for all inbox-field / extraction-schema field types.
 *
 * - text / textarea / number / email / phone / url: controlled input that
 *   fires `onChange` per keystroke.
 * - boolean / select / date: fires `onChange` on interaction.
 * - file: uploads to storage and fires `onChange` with the resulting ref.
 * - table: renders `TableFieldGrid`; fires `onChange` per cell keystroke
 *   and per row add/remove.
 *
 * The caller is responsible for persistence — debounce server saves as
 * appropriate.
 */
export const FieldWidget = ({
  field,
  value,
  onChange,
  readOnly,
  compact,
  onDraftChange,
  invalid,
  tableWidthStorageKey,
}: FieldWidgetProps) => {
  const ringClass = invalidRingClass(invalid);
  // One unified rendering tree — the `readOnly` branch passes `disabled`
  // (or equivalents) to every input rather than swapping in a different
  // read-only view. Same layout, no caret, dimmed look for every type.
  const rowClass = "flex items-center gap-2 h-8";
  const isDisabled = !!readOnly;

  switch (field.type) {
    case "boolean": {
      const checked = !!value;
      return (
        <div className={rowClass}>
          <Switch
            checked={checked}
            onCheckedChange={(c) => onChange(c)}
            disabled={isDisabled}
          />
          <span className="text-sm text-muted-foreground">
            {checked ? "Yes" : "No"}
          </span>
        </div>
      );
    }

    case "select": {
      const lookupSource = getFieldLookupSource(field);
      if (lookupSource) {
        // Fill of sibling fields happens server-side on save; the widget
        // only needs to commit its own value.
        return (
          <div className={rowClass}>
            <div
              className={`h-8 w-full min-w-[120px] rounded-md border border-input bg-transparent${ringClass}`}
            >
              <LookupCellCombobox
                source={lookupSource}
                value={value}
                readOnly={isDisabled}
                onSelect={(selected) => onChange(selected)}
              />
            </div>
          </div>
        );
      }
      const opts = getSelectOptions(field);
      const stringValue =
        value != null && value !== "" ? String(value) : SELECT_NONE;
      return (
        <div className={rowClass}>
          <Select
            value={stringValue}
            onValueChange={(v) => onChange(v === SELECT_NONE ? null : v)}
            disabled={isDisabled}
          >
            <SelectTrigger
              className={`h-8 ${compact ? "w-full" : "w-fit min-w-[120px]"}${ringClass}`}
            >
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SELECT_NONE}>-</SelectItem>
              {opts.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    case "date": {
      const validDate = parseDateOnly(value);
      const displayDate = validDate ? validDate.toLocaleDateString() : "-";
      return (
        <div className={rowClass}>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                disabled={isDisabled}
                className={`h-8 ${compact ? "w-full" : "w-fit min-w-[180px]"} justify-start text-sm font-normal text-muted-foreground${ringClass}`}
              >
                <Calendar className="h-3 w-3 text-muted-foreground/60" />
                {displayDate}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                mode="single"
                selected={validDate}
                onSelect={(date) => {
                  onChange(date ? formatDateOnly(date) : null);
                }}
              />
            </PopoverContent>
          </Popover>
        </div>
      );
    }

    case "textarea": {
      // Commit on blur — one save per editing session. `key={stringValue}`
      // remounts the editor when an external value changes (e.g. a
      // server-side update), so it picks up the new content.
      const stringValue = String(value ?? "");
      if (field.render_markdown) {
        return (
          <div className={ringClass ? `rounded-md${ringClass}` : undefined}>
            <MarkdownFieldCell
              key={stringValue}
              initialValue={stringValue}
              readOnly={isDisabled}
              onCommit={(v) => onChange(v)}
            />
          </div>
        );
      }
      return (
        <Textarea
          key={stringValue}
          defaultValue={stringValue}
          disabled={isDisabled}
          onBlur={(e) => {
            if (e.target.value !== stringValue) onChange(e.target.value);
          }}
          placeholder="-"
          className={cn(
            "min-h-[80px] max-h-[180px] text-sm resize-y overflow-auto border-0 shadow-[0_0_0_1px_var(--input),0_1px_2px_0_rgba(0,0,0,0.05)] bg-transparent hover:shadow-[0_0_0_1px_var(--border),0_1px_2px_0_rgba(0,0,0,0.05)] focus:bg-background transition-colors disabled:opacity-60 disabled:cursor-not-allowed",
            ringClass
          )}
        />
      );
    }

    case "file":
      return (
        <FileFieldCell
          value={value}
          onChange={onChange}
          readOnly={isDisabled}
        />
      );

    case "table": {
      const columns = getTableColumns(field);
      const rows = normalizeTableRows(value, columns);
      return (
        <TableFieldGrid
          columns={columns}
          rows={rows}
          onChange={(newRows) => onChange(newRows)}
          readOnly={isDisabled}
          widthStorageKey={tableWidthStorageKey}
        />
      );
    }

    case "text":
    case "number":
    case "email":
    case "phone":
    case "url":
    default: {
      const inputType = INPUT_TYPE_BY_FIELD[field.type] ?? "text";
      const stringValue = String(value ?? "");

      const commit = (raw: string) => {
        if (raw === stringValue) return;
        if (field.type === "number") {
          onChange(raw === "" ? null : Number(raw));
        } else {
          onChange(raw);
        }
      };

      return (
        <div className={rowClass}>
          <Input
            // Uncontrolled with onBlur: one save per editing session, no
            // per-keystroke server hits. `key={stringValue}` remounts the
            // input when an external value changes (e.g. polling), so
            // `defaultValue` reflects it.
            key={stringValue}
            type={inputType}
            // Long values scroll out of the input; hover shows the whole value.
            title={stringValue || undefined}
            inputMode={field.type === "phone" ? "tel" : undefined}
            pattern={field.type === "phone" ? "[+0-9\\s\\-()]*" : undefined}
            defaultValue={stringValue}
            disabled={isDisabled}
            onChange={(e) => {
              if (field.type === "phone") {
                e.target.value = e.target.value.replace(/[^+0-9\s\-()]/g, "");
              }
              // Uncontrolled input: keep the hover text in step with what is
              // typed, not with the last saved value.
              e.target.title = e.target.value;
              if (onDraftChange) {
                const raw = e.target.value;
                onDraftChange(
                  field.type === "number"
                    ? raw === ""
                      ? null
                      : Number(raw)
                    : raw
                );
              }
            }}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
            }}
            placeholder="-"
            className={`h-8 ${compact ? "w-full" : "flex-1"} text-sm border-0 shadow-[0_0_0_1px_var(--input),0_1px_2px_0_rgba(0,0,0,0.05)] rounded-md px-2 bg-transparent hover:shadow-[0_0_0_1px_var(--border),0_1px_2px_0_rgba(0,0,0,0.05)] focus:bg-background focus:ring-1 focus:ring-border transition-all disabled:opacity-60 disabled:cursor-not-allowed${ringClass}`}
          />
        </div>
      );
    }
  }
};
